import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { caseStudies } from '../data/caseStudies';
import SEO from '../components/SEO';

function CaseStudies() {
  const { t, language } = useLanguage();

  const pageTitle = {
    sq: 'Case Studies - Histori Suksesi',
    en: 'Case Studies - Success Stories'
  };

  const pageSubtitle = {
    sq: 'Rezultate reale nga klientë realë. Shiko si i kemi ndihmuar bizneset të rriten.',
    en: 'Real results from real clients. See how we helped businesses grow.'
  };

  return (
    <>
      <SEO 
        title={pageTitle[language]}
        description={pageSubtitle[language]}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Hero Section */}
        <div className="relative py-20 sm:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 via-transparent to-purple-500/10"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-block mb-6"
              >
                <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                  {language === 'sq' ? 'Histori Suksesi' : 'Success Stories'}
                </div>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                {pageTitle[language]}
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8">
                {pageSubtitle[language]}
              </p>

              <div className="flex flex-wrap justify-center gap-8 pt-8">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">€250K+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {language === 'sq' ? 'Revenue e Gjeneruar' : 'Revenue Generated'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">+580%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {language === 'sq' ? 'Rritje Mesatare' : 'Average Growth'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">15+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {language === 'sq' ? 'Projekte të Suksesshme' : 'Successful Projects'}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Case Studies Grid */}
        <div className="container mx-auto px-4 pb-20">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}>
                  {/* Image */}
                  <div className="w-full lg:w-1/2">
                    <div className="relative group overflow-hidden rounded-2xl shadow-2xl">
                      <img 
                        src={study.thumbnail} 
                        alt={study.client}
                        className="w-full h-[400px] object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {study.liveUrl && study.liveUrl !== '#' && (
                        <a 
                          href={study.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute bottom-4 left-4 right-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                        >
                          <ExternalLink className="w-5 h-5" />
                          {language === 'sq' ? 'Shiko Live' : 'View Live'}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="w-full lg:w-1/2">
                    <div className="space-y-6">
                      {/* Category Badge */}
                      <div className="inline-block bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-4 py-1 rounded-full text-sm font-semibold">
                        {study.category}
                      </div>

                      {/* Client & Industry */}
                      <div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                          {study.client}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400">
                          {study.industry} • {study.location}
                        </p>
                      </div>

                      {/* Challenge Preview */}
                      <div 
                        className="text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: study.challenge[language] }}
                      />

                      {/* Metrics Preview */}
                      <div className="grid grid-cols-2 gap-4">
                        {study.results.metrics.slice(0, 4).map((metric, idx) => (
                          <div key={idx} className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">
                              {metric.value}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {metric.label[language]}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      <Link 
                        to={`/case-study/${study.slug}`}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                      >
                        {language === 'sq' ? 'Lexo Case Study' : 'Read Case Study'}
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-3xl p-8 sm:p-12 text-center shadow-2xl"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {language === 'sq' ? 'Gati për Rezultate si Këto?' : 'Ready for Results Like These?'}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {language === 'sq' 
                ? 'Le të diskutojmë se si mund t\'ju ndihmojmë të arrini qëllimet tuaja.' 
                : 'Let\'s discuss how we can help you achieve your goals.'}
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-yellow-600 px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              {language === 'sq' ? 'Kontaktoni Sot' : 'Contact Us Today'}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default CaseStudies;